import { tokenLifeTimeSeconds } from "../config";

/**
 * Direct calls to the auth server (not the portal API).
 *
 * Deliberately uses bare `fetch`, not the shared Axios instance: that one has
 * a baseURL pointing at the portal server and interceptors that redirect on
 * 401. Here a 401 is often meaningful data — "the session is already gone",
 * "you may not read locks" — and must reach the caller intact.
 *
 * The auth server returns PLAIN TEXT bodies for most errors even though the
 * Content-Type header says JSON, so never call res.json() before checking
 * res.ok — it throws SyntaxError and the failure disappears.
 */

export interface LockRecord {
  _id: string;
  scope: "account" | "ip" | "accountIp";
  /** Canonical dedup key, e.g. "account:jane". */
  target: string;
  username?: string;
  connectingIp?: string;
  userAgent?: string;
  /** Cumulative since the last success or admin release. */
  failedCount: number;
  /** How many times this target has been locked — drives escalation. */
  lockCount: number;
  locked: boolean;
  firstFailedAt?: string;
  lastFailedAt?: string;
  lockedAt?: string;
  /** Absent under the untilAdminUnlock policy: it never lapses on its own. */
  expiredAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AppAccess {
  clientId: string;
  username: string;
  access: {
    role?: string;
    pages?: string[];
    [key: string]: unknown;
  };
}

const readBody = async (res: Response): Promise<unknown> => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const messageFrom = (body: unknown, fallback: string): string => {
  if (body && typeof body === "object" && "message" in body) {
    return String((body as { message: unknown }).message);
  }
  if (typeof body === "string" && body) return body;
  return fallback;
};

/**
 * `readBody` is text-first, so a body can be an object, a bare string or null —
 * and `"x" in body` throws a TypeError on a primitive. Funnel every body
 * through here before reading fields off it.
 */
const asRecord = (body: unknown): Record<string, unknown> =>
  typeof body === "object" && body !== null
    ? (body as Record<string, unknown>)
    : {};

/**
 * Why sign-in failed, when the server named a cause specific enough to act on.
 *
 * Mutually exclusive by construction: the auth server checks password expiry
 * before it reaches the MFA branch. Modelled as a union rather than a set of
 * booleans so no illegal combination is representable — and because the
 * previous `blocked` flag named the UI treatment rather than the cause, which
 * is how its doc comment drifted into claiming it covered password expiry.
 */
export type LoginFailureReason = "locked" | "passwordExpired" | "mfaRequired";

export interface LoginResult {
  token?: string;
  error?: string;
  /** Set only alongside `error`. Drives which hint the login page shows. */
  reason?: LoginFailureReason;
}

export const login = async (
  authServer: string,
  audience: string,
  username: string,
  password: string
): Promise<LoginResult> => {
  try {
    const res = await fetch(`${authServer}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        username,
        password,
        allowRefresh: true,
        audience,
        // Server-side backstop for the inactivity timeout: if the user just
        // closes the tab, no client code runs to sign them out, so the session
        // has to lapse on its own. The auth server clamps this to its own
        // maxTokenLifeTime and carries it across every refresh.
        lifeTime: tokenLifeTimeSeconds(),
      }),
    });

    const body = await readBody(res);

    if (!res.ok) {
      // 402 carries the real reason — "this user account is locked. Please
      // contact an administrator to unlock it". Surfacing it verbatim is the
      // whole point: a locked user who only sees "invalid username or
      // password" will keep trying and stay locked.
      if (res.status === 402) {
        return {
          error: messageFrom(body, "Your account is locked."),
          reason: "locked",
        };
      }
      return { error: messageFrom(body, "Invalid username or password") };
    }

    // POST /token answers 201 for THREE outcomes and only one carries a token:
    // a signed JWT, `{passwordExpired:true}`, or `{mfaEnabled, sessionId}`.
    // Reading `token` and calling everything else a protocol error is what
    // surfaced "No token in server response" to users with expired passwords.
    const ok = asRecord(body);

    // Token first. If the server ever grants a grace-period login alongside a
    // flag, honour the token it just minted rather than locking out a user it
    // had authorised — fail open on a flag we do not control.
    const token = typeof ok.token === "string" ? ok.token : "";
    if (token) return { token };

    // Correct password, but older than the server's `passwordRequirements
    // .maxAge`. The body is a bare flag — no message, no code — so the copy is
    // ours. Deliberately not routed through `messageFrom`: that would look
    // right today and start piping unreviewed server text into the UI the day
    // this body gains a `message`.
    if (ok.passwordExpired === true) {
      return {
        error:
          "Your password has expired. Please contact technical support to reset it.",
        reason: "passwordExpired",
      };
    }

    // MFA is enabled on the account, but this portal has no second-factor step
    // to send the user to. A hard stop, not a prompt.
    if (ok.mfaEnabled === true) {
      return {
        error:
          "This account requires two-factor authentication, which the EMMS portal does not support. Please contact technical support.",
        reason: "mfaRequired",
      };
    }

    // Now genuinely a protocol violation rather than a shape we forgot about.
    return { error: "No token in server response" };
  } catch {
    return { error: "Cannot reach the authentication server" };
  }
};

/**
 * Ends the session server-side. Without this, logging out only drops the
 * local copy of the token: it stays valid until `exp` and the session feed
 * keeps advertising it, so anyone holding it stays logged in.
 */
export const logout = async (authServer: string, token: string): Promise<void> => {
  try {
    await fetch(`${authServer}/token/-/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch {
    // Best effort. A 401 means the session was already gone, and a network
    // failure must not trap the user in a session they asked to leave — the
    // caller clears local state either way.
  }
};

/** This user's access for this application, as granted on the auth server. */
export const getMyAppAccess = async (
  authServer: string,
  token: string
): Promise<AppAccess | null> => {
  try {
    const res = await fetch(`${authServer}/appAccess/-/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as AppAccess;
  } catch {
    return null;
  }
};

/** Locked accounts. Requires the caller to hold `/locks` on the auth server. */
export const listLocks = async (
  authServer: string,
  token: string
): Promise<{ locks?: LockRecord[]; error?: string }> => {
  try {
    const res = await fetch(`${authServer}/locks?active=true`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await readBody(res);
    if (!res.ok) {
      if (res.status === 403) {
        return {
          error:
            "Your account is not permitted to read locked accounts. Ask an auth-server administrator to grant the /locks privilege.",
        };
      }
      return { error: messageFrom(body, "Could not load locked accounts") };
    }
    return { locks: body as LockRecord[] };
  } catch {
    return { error: "Cannot reach the authentication server" };
  }
};

export const releaseLock = async (
  authServer: string,
  token: string,
  lockId: string
): Promise<{ released?: number; error?: string }> => {
  try {
    // POST, not DELETE: the auth server's body parser skips DELETE, so every
    // mutating route there is POST.
    const res = await fetch(`${authServer}/locks/${lockId}/release`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await readBody(res);
    if (!res.ok) return { error: messageFrom(body, "Could not unlock this account") };
    return { released: (body as { released?: number } | null)?.released ?? 0 };
  } catch {
    return { error: "Cannot reach the authentication server" };
  }
};
